import { lazy, Suspense, useMemo, useCallback, memo } from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { THEME_CONFIGS } from '../../../../config/themes';

// Dynamically import TinyMCE Editor to reduce initial bundle size (bundle-dynamic-imports)
const TinyMCEEditor = lazy(() => 
  import('@tinymce/tinymce-react').then(mod => {
    const Editor = mod.Editor;
    return {
      default: (props: any) => <Editor {...props} />
    };
  })
);

// rendering-hoist-jsx: Extract static configurations outside component (rerender-memo-with-default-value)
const TINYMCE_PLUGINS = [
  "advlist",
  "autolink",
  "lists",
  "link",
  "image",
  "charmap",
  "preview",
  "anchor",
  "searchreplace",
  "visualblocks",
  "code",
  "codesample",
  "fullscreen",
  "insertdatetime",
  "media",
  "table",
  "help",
  "wordcount",
];

const TINYMCE_TOOLBAR = 
  "undo redo | blocks | " +
  "bold italic forecolor | alignleft aligncenter " +
  "alignright alignjustify | bullist numlist outdent indent | " +
  "removeformat code codesample | help";

// rendering-hoist-jsx: Hoist static JSX fallback
const EDITOR_FALLBACK = <div className="p-3">Loading editor...</div>;

interface LazyEditorProps {
  onEditorChange: (content: string) => void;
  editorRef: React.MutableRefObject<any>;
  initialValue?: string;
  keyEditor: number;
}

// rerender-memo: Extract expensive work into memoized components
const LazyEditorComponent = memo(({ onEditorChange, editorRef, initialValue, keyEditor }: LazyEditorProps) => {
  const { currentTheme } = useTheme();
  const themeConfig = THEME_CONFIGS[currentTheme];
  
  // js-cache-property-access: Cache frequently accessed properties
  const { colors, mode } = themeConfig;
  const isDark = mode === 'dark';
  
  // rerender-functional-setstate: Use stable callback for onInit (advanced-event-handler-refs)
  const handleEditorInit = useCallback((_evt: any, editor: any) => {
    editorRef.current = editor;
  }, [editorRef]);

  // js-cache-function-results: Create helper function for color with opacity
  const getColorWithOpacity = useCallback((color: string, opacity: number) => {
    // Convert hex to rgba if needed
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    // If already rgba/rgb, adjust opacity
    if (color.startsWith('rgb')) {
      return color.replace(/rgba?\([^)]+\)/, color.replace(/,\s*[\d.]+\)$/, `, ${opacity})`));
    }
    return color;
  }, []);

  // Generate theme-aware TinyMCE configuration (rerender-dependencies: Use primitive dependencies)
  const editorConfig = useMemo(() => {
    // Create theme-consistent background colors with proper opacity
    const subtleBackground = getColorWithOpacity(colors.primary, 0.08);
    const codeBackground = getColorWithOpacity(colors.primary, 0.12);
    const tableHeaderBackground = getColorWithOpacity(colors.text, 0.08);
    
    return {
      height: "100%",
      skin: isDark ? 'oxide-dark' : 'oxide',
      content_css: isDark ? 'dark' : 'default',
      menubar: false,
      plugins: TINYMCE_PLUGINS,
      toolbar: TINYMCE_TOOLBAR,
      content_style: `
        body { 
          font-family: Helvetica, Arial, sans-serif; 
          font-size: 14px;
          color: ${colors.text};
          background-color: ${colors.surface};
          margin: 8px;
        }
        h1, h2, h3, h4, h5, h6 { 
          color: ${colors.text}; 
        }
        a { 
          color: ${colors.primary}; 
        }
        a:hover {
          color: ${getColorWithOpacity(colors.primary, 0.8)};
        }
        blockquote {
          border-left: 4px solid ${colors.primary};
          margin: 1.5em 0;
          padding: 0.5em 0 0.5em 1em;
          background-color: ${subtleBackground};
          color: ${colors.text};
          font-style: italic;
        }
        code {
          background-color: ${codeBackground};
          color: ${colors.primary};
          padding: 2px 4px;
          border-radius: 3px;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        }
        pre {
          background-color: ${subtleBackground};
          border: 1px solid ${colors.border};
          color: ${colors.text};
          padding: 10px;
          border-radius: 4px;
          overflow-x: auto;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        }
        pre code {
          background-color: transparent;
          color: inherit;
          padding: 0;
        }
        table {
          border-collapse: collapse;
          width: 100%;
          margin: 1em 0;
        }
        table td, table th {
          border: 1px solid ${colors.border};
          padding: 8px 12px;
          text-align: left;
        }
        table th {
          background-color: ${tableHeaderBackground};
          font-weight: 600;
          color: ${colors.text};
        }
        table tr:nth-child(even) {
          background-color: ${getColorWithOpacity(colors.background, 0.5)};
        }
        hr {
          border: none;
          border-top: 2px solid ${colors.border};
          margin: 1.5em 0;
        }
        ul, ol {
          color: ${colors.text};
          padding-left: 1.2em;
        }
        li {
          margin: 0.3em 0;
        }
      `,
    };
  }, [colors.text, colors.surface, colors.primary, colors.border, colors.background, getColorWithOpacity, isDark]); // rerender-dependencies: primitive dependencies

  return (
    <Suspense fallback={EDITOR_FALLBACK}>
      <TinyMCEEditor
        key={keyEditor}
        onEditorChange={onEditorChange}
        apiKey="9k50r9zl7x8x55lb5v10xen2q77qywsqn9sec7xbxlj0409d"
        onInit={handleEditorInit}
        initialValue={initialValue}
        init={editorConfig}
      />
    </Suspense>
  );
});

LazyEditorComponent.displayName = 'LazyEditor';

export const LazyEditor = LazyEditorComponent;

export default LazyEditor;
